import { Box, Center, HStack, Spacer, Spinner, Stack, Text } from '@chakra-ui/react'
import lowerFirst from 'lodash/lowerFirst'
import type { FC } from 'react'
import { memo } from 'react'

import { Button } from '@app/components/ui/button'
import { toaster } from '@app/components/ui/toaster'
import useInteraction from '@app/pages/strategies/useInteraction'
import BasicCard from '@app/shared/cards/basicCard/basicCard'
import EntityCard from '@app/shared/cards/entity/entityCard'
import MetricCard from '@app/shared/cards/metric/metricCard'
import SubMapCard from '@app/shared/cards/subMap/subMapCard'
import type { DomainObject } from '@app/types'
import { useStrategyBulkAddExistingCardsMutation } from '@graphql/queries'

type Props = {
  strategyId: string
  query: string
  objects: (DomainObject & { rfId: string })[]
  loading?: boolean
}

const ExistingCardsList: FC<Props> = ({ strategyId, query, objects, loading = false }) => {
  const { onDragStart } = useInteraction(strategyId)
  const resultsCount = objects?.length || 0
  const noSearch = !query
  const noResults = !noSearch && resultsCount === 0
  const [, strategyBulkAdd] = useStrategyBulkAddExistingCardsMutation()

  if (noSearch && resultsCount === 0) {
    return (
      <Center h="50vh" data-testid="existing-cards-list-no-fetched-data">
        {loading ? (
          <Spinner />
        ) : (
          <Text color="subtle" fontWeight="light" textAlign="center">
            Use the filters above to search for existing cards.
          </Text>
        )}
      </Center>
    )
  }

  const handleBulkAdd = async () => {
    const classType = objects[0].classType

    await strategyBulkAdd({
      input: {
        strategyId,
        domainObjectType: classType,
        domainObjectIds: objects.map((object) => object.id)
      }
    })

    toaster.create({
      title: 'Adding all to the map',
      description: "The cards you've selected will be added to the map shortly.",
      type: 'info'
    })
  }

  return (
    <Stack gap={4} pb={6} data-testid="existing-cards-list">
      <HStack>
        {loading ? (
          <Spinner />
        ) : (
          <Text color="muted" data-testid="number-results">
            {resultsCount} found
          </Text>
        )}
        <Spacer />
        {objects?.length > 1 && (
          <Button size="xs" variant="subtle" onClick={handleBulkAdd} disabled={loading}>
            Add all
          </Button>
        )}
      </HStack>
      {objects &&
        objects.map((object) => {
          const { classType } = object
          let CardType

          switch (classType) {
            case 'entity':
              CardType = EntityCard
              break
            case 'metric':
              CardType = MetricCard
              break
            case 'strategy':
              CardType = SubMapCard
              break
            case 'basicCard':
              CardType = BasicCard
              break
            default:
          }

          if (!CardType) {
            return null
          }

          const cardData = {}
          // Strip out the recentActivity field from the card data, not useful here
          cardData[lowerFirst(classType)] = { ...object, recentActivity: false }

          return (
            <CardType
              key={`existing-card-${object.rfId}`}
              draggable
              onDragStart={(e) => onDragStart(e, object)}
              _grabbed={{ cursor: 'grabbing' }}
              _hover={{ cursor: 'grab' }}
              className={`react-flow__placed-card drag-drop-card-${object.rfId} unselectable-children`}
              strategyId={strategyId}
              mapMode="build"
              {...cardData}
              zIndex="modal" // This ensures that the element being draggable is above everything else so the browser can create a proper preview
            />
          )
        })}
      {noResults && <Box>No results found</Box>}
    </Stack>
  )
}

ExistingCardsList.displayName = 'ExistingCardsList'

export default memo(ExistingCardsList)
